import React, { memo, useMemo, useState, useEffect } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon } from "react-leaflet";
import { format } from "date-fns";

import { ConnectProto, GeoProcessType } from "@racoon/api";
import { Namespace, useT, useDateFnsLocale } from "@racoon/i18n";
import { MAP_TILE_CONFIG } from "@racoon/lib";
import {
  TimelinePlayer,
  type TimelineSegment,
  findPositionAtTime,
  travelPathToTimelinePoints,
  type TravelPathLike,
} from "@racoon/ui";

import { makeTruckIcon } from "~/shared/utils";
import {
  getProviderColor,
  TruckProviderGroup,
} from "~/pages/dashboard/lib/use-trucks-by-provider.hook";
import { convertGeoJsonToLeaflet } from "../utils/coordinateConversion";

const DEFAULT_CENTER: [number, number] = [43.238949, 76.940088];

const TruckMarkerComponent = memo(
  ({
    position,
    truck,
    truckProviderGroups = [],
  }: {
    position: [number, number];
    truck: ConnectProto.GovTruckInfo;
    truckProviderGroups?: TruckProviderGroup[];
  }) => {
    const providerColor = useMemo(
      () => getProviderColor(truckProviderGroups, truck.serviceProvider?.id),
      [truck.serviceProvider?.id, truckProviderGroups],
    );

    const icon = useMemo(
      () => makeTruckIcon(truck, { isSelected: true, providerColor }),
      [truck, providerColor],
    );
    return <Marker position={position} icon={icon as L.Icon} />;
  },
  (prev, next) =>
    prev.position[0] === next.position[0] &&
    prev.position[1] === next.position[1] &&
    prev.truck.angle === next.truck.angle &&
    String(prev.truck.serviceProvider?.id) === String(next.truck.serviceProvider?.id),
);
TruckMarkerComponent.displayName = "TruckMarker";

const extractPathCoords = (
  geojsonFeature: Record<string, any> | undefined,
): [number, number][][] => {
  if (!geojsonFeature) return [];

  const geometry = geojsonFeature.geometry;

  if (!geometry) return [];

  if (geometry.type === "LineString") {
    const coords = convertGeoJsonToLeaflet(geometry.coordinates);
    return coords.length > 0 ? [coords] : [];
  }

  if (geometry.type === "MultiLineString") {
    return (geometry.coordinates as [number, number][][])
      .map((line: [number, number][]) => convertGeoJsonToLeaflet(line))
      .filter((c) => c.length > 0);
  }

  return [];
};

interface AnomaliesMapProps {
  anomaly: any;
  onMarkerClick?: (anomaly: any) => void;
  travelPath?: ConnectProto.GovTruckTravelPathResponse;
}

export const AnomaliesMap: React.FC<AnomaliesMapProps> = ({
  anomaly,
  onMarkerClick,
  travelPath,
}) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();
  const [playbackTime, setPlaybackTime] = useState<number | null>(null);

  const pathLines = useMemo(() => extractPathCoords(travelPath?.geojsonFeature), [travelPath]);

  const timelinePoints = useMemo(
    () => travelPathToTimelinePoints(travelPath?.geojsonFeature as TravelPathLike | undefined),
    [travelPath],
  );

  // Reset playback when the selected anomaly changes
  useEffect(() => {
    setPlaybackTime(null);
  }, [anomaly.id]);

  const playbackPosition = useMemo(() => {
    if (playbackTime === null || timelinePoints.length === 0) return null;
    return findPositionAtTime(timelinePoints, playbackTime);
  }, [playbackTime, timelinePoints]);

  const traveledPath = useMemo((): [number, number][] => {
    if (playbackTime === null || timelinePoints.length === 0) return [];
    const traveled = timelinePoints
      .filter((p) => p.timestamp <= playbackTime)
      .map((p) => [p.lat, p.lon] as [number, number]);
    if (playbackPosition) traveled.push([playbackPosition.lat, playbackPosition.lon]);
    return traveled;
  }, [timelinePoints, playbackTime, playbackPosition]);

  const center: [number, number] =
    anomaly.lat && anomaly.lon ? [Number(anomaly.lat), Number(anomaly.lon)] : DEFAULT_CENTER;

  const markerPosition: [number, number] = playbackPosition
    ? [playbackPosition.lat, playbackPosition.lon]
    : timelinePoints.length > 0
      ? [timelinePoints[0].lat, timelinePoints[0].lon]
      : center;

  const anomalyStartTime = useMemo(() => {
    if (!anomaly.gpsStartAt?.seconds) return null;
    return format(new Date(Number(anomaly.gpsStartAt.seconds) * 1000), "HH:mm dd MMM yyyy", {
      locale: dateLocale,
    });
  }, [anomaly.gpsStartAt?.seconds, dateLocale]);

  const anomalySegments = useMemo<TimelineSegment[]>(() => {
    const startSeconds = anomaly.gpsStartAt?.seconds ? Number(anomaly.gpsStartAt.seconds) : null;
    const endSeconds = anomaly.gpsEndAt?.seconds ? Number(anomaly.gpsEndAt.seconds) : null;

    if (!startSeconds || !endSeconds || timelinePoints.length === 0) return [];

    const minTime = timelinePoints[0]?.timestamp ?? 0;
    const maxTime = timelinePoints[timelinePoints.length - 1]?.timestamp ?? 0;

    const segments: TimelineSegment[] = [];

    // Before anomaly — white
    if (startSeconds * 1000 > minTime) {
      segments.push({
        startTime: minTime,
        endTime: startSeconds * 1000,
        processType: GeoProcessType.GEO_PROCESS_TYPE_TRANSIT_PROCESS,
        color: "#FFFFFF",
        label: "",
      });
    }

    // Anomaly — red
    segments.push({
      startTime: startSeconds * 1000,
      endTime: endSeconds * 1000,
      processType: GeoProcessType.GEO_PROCESS_TYPE_TRANSIT_PROCESS,
      color: "#DC2626",
      label: t(Namespace.Govdash, "anomalies.anomaly") || "Аномалия",
    });

    // After anomaly — white
    if (endSeconds * 1000 < maxTime) {
      segments.push({
        startTime: endSeconds * 1000,
        endTime: maxTime,
        processType: GeoProcessType.GEO_PROCESS_TYPE_TRANSIT_PROCESS,
        color: "#FFFFFF",
        label: "",
      });
    }

    return segments;
  }, [anomaly.gpsStartAt?.seconds, anomaly.gpsEndAt?.seconds, timelinePoints, t]);

  const durationOnSite = useMemo(() => {
    const startSeconds = anomaly.gpsStartAt?.seconds ? Number(anomaly.gpsStartAt.seconds) : 0;
    const endSeconds = anomaly.gpsEndAt?.seconds ? Number(anomaly.gpsEndAt.seconds) : 0;

    if (!startSeconds || !endSeconds) return null;

    const durationSeconds = Math.floor(endSeconds - startSeconds);
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;

    return t(Namespace.Govdash, "anomalies.duration", { minutes, seconds });
  }, [anomaly.gpsStartAt?.seconds, anomaly.gpsEndAt?.seconds, t]);

  return (
    <div className="relative h-full w-full">
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: "100%", width: "100%", borderRadius: "8px" }}
        className="rounded-[8px] z-0"
        zoomControl={false}
      >
        <TileLayer
          url={MAP_TILE_CONFIG.url}
          subdomains={MAP_TILE_CONFIG.subdomains}
          attribution={MAP_TILE_CONFIG.attribution}
        />

        <div className="absolute z-[1000] top-4 left-4 rounded-[8px] bg-[#303338] p-[8px] text-[12px] font-medium leading-[16px] text-[#f2f4f7] tracking-[-0.12px]">
          <div className="flex gap-[8px] items-start">
            <p>{t(Namespace.Govdash, "anomalies.time") || "Время аномалии"}:</p>
            <p className="font-medium">{anomalyStartTime}</p>
          </div>
          <div className="mt-[8px] flex gap-[8px] items-start">
            <p>{t(Namespace.Govdash, "anomalies.wasOnSection") || "Находился на участке:"}</p>
            <p className="font-medium">{durationOnSite}</p>
          </div>
        </div>

        {/* Full path — dimmed when playback is active */}
        {pathLines.map((line, i) => (
          <Polyline
            key={i}
            positions={line}
            pathOptions={{
              color: "#2563EB",
              weight: 3,
              opacity: playbackTime !== null ? 0.25 : 0.8,
            }}
          />
        ))}

        {/* Traveled portion drawn over the full path */}
        {traveledPath.length > 1 && (
          <Polyline
            positions={traveledPath}
            pathOptions={{ color: "#2563EB", weight: 3, opacity: 1 }}
          />
        )}

        {anomaly.location?.geometry?.coordinates?.length > 0 && (
          <>
            {anomaly.location?.geometry?.coordinates.map((coordinates: any, index: number) => {
              const positions = convertGeoJsonToLeaflet(coordinates);

              if (positions.length === 0) {
                return null;
              }

              return (
                <Polygon
                  key={index}
                  positions={positions}
                  pathOptions={{
                    color: "#DC2626",
                    fill: true,
                    fillColor: "#FCA5A5",
                    fillOpacity: 0.4,
                    weight: 2,
                  }}
                >
                  <Popup closeButton={false}>
                    <div
                      className="text-sm font-semibold cursor-pointer hover:underline"
                      onClick={() => {
                        onMarkerClick?.(anomaly);
                      }}
                    >
                      {t(Namespace.Govdash, "anomalies.unauthorizedStop")}
                    </div>
                  </Popup>
                </Polygon>
              );
            })}
          </>
        )}

        <TruckMarkerComponent
          position={markerPosition}
          truck={{
            ...anomaly,
            serviceProvider: anomaly.ownerOrganization,
            angle: playbackPosition?.angle ?? timelinePoints[0]?.angle,
          }}
        />
      </MapContainer>

      {timelinePoints.length > 0 && (
        <div className="absolute bottom-4 left-4 right-4 z-[1000]">
          <TimelinePlayer
            points={timelinePoints}
            segments={anomalySegments}
            onTimeChange={setPlaybackTime}
          />
        </div>
      )}
    </div>
  );
};
